import { Component, OnInit, OnDestroy } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators, ValidationErrors } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';

import { ClosableModalWithErrorResponseModel } from '../../../shared/components/base/closable-modal-with-error-response-model';
import {
  NotificationService,
  CompSearchService,
  SessionService,
  UtilsService,
  EntitiesService,
  TpSafeService,
  CurrenciesService,
  CountriesService,
  ReportRepositoryService
} from '../../../shared/services';
import { ModalService } from '../../../shared/modules/modal';
import {
  Container,
  FinancialDataValueEnteredInEnum,
  Entity,
  Transaction,
  LegalEntityTransaction,
  BaseOption,
  LanguageIsoInfo,
  LocalFileCreateModalPayload,
  Currency,
  Country,
} from '../../../shared/models';
import { UploadReportVersionPayload } from '../../../shared/models/request-payload/upload-report-version-payload';
import { PaginationConstants } from '../../../shared/constants';
import { FileConstants } from '../../../shared/constants/file';
import { ObjectResponse } from '../../../shared/interfaces';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'upload-report-version-modal',
  templateUrl: './upload-local-file-report-modal.component.html',
  styleUrls: ['./upload-local-file-report-modal.scss']
})
export class UploadLocalFileReportModalComponent
  extends ClosableModalWithErrorResponseModel<any>
  implements OnInit, OnDestroy {

  data: LocalFileCreateModalPayload;
  subscriptions: Subscription[] = [];
  languages: LanguageIsoInfo[];
  currencyOptions: BaseOption[];
  potentialTradingPartners: Entity[];
  controlledTransactionsInReport: Transaction[];
  uploadReportVersionForm: FormGroup;
  previousSelectedTradingPartnerIds: number[] = [];
  viewedTransactions: Transaction[];
  totalItems: number;
  selectableTransactions: Transaction[];
  primaryEntityList: Entity[] = [];
  selectedTradingPartnerIds: number[] = [];
  selectedPrimaryEntityIds: number[] = [];
  tradingPartnerEntities: { [key: number]: Entity } = {};
  primaryTradingPartnerMap: { [key: number]: number[] } = {};
  primaryTradingPartnerTransactionMap: Record<string, Transaction[]> = {};
  domicile: string;
  firstPrimaryEntity: Entity;

  transactionsOnCurrentPage: Transaction[];
  itemsPerPage: number;
  countryId: number;

  private uploadedReport: File;
  private workingContainer: Container;

  private onDestroy$: Subject<null>;
  mneCurrency: Currency;
  taxJurisdictionCurrency: Currency;
  upe: Entity;
  tradingPartnerId: number;

  executiveSummaryIntro: string = '';
  executiveSummaryConclusion: string = '';
  shouldDisplayPageNumber: boolean;

  constructor(
    protected notificationService: NotificationService,
    protected modalService: ModalService,
    private _formBuilder: FormBuilder,
    private _sessionService: SessionService,
    private _compSearchService: CompSearchService,
    private _utilsService: UtilsService,
    private _entitiesService: EntitiesService,
    private _tpSafeService: TpSafeService,
    private _currenciesService: CurrenciesService,
    private _countriesService: CountriesService,
    private _reportService: ReportRepositoryService,
    private _translateService: TranslateService
  ) {
    super(notificationService, modalService);
  }

  ngOnInit() {
    this.uploadReportVersionForm = this._formBuilder.group({
      file: ['', [Validators.required, this.validateFile.bind(this)]],
      language: ['', Validators.required],
      currencyType: ['', Validators.required],
      tradingPartnerEntityIds: [[], Validators.required],
      reportStage: ['', Validators.required],
      primaryEntityIds: [[], Validators.required],
      controlledTransactionsInReport: this._formBuilder.group({}, { validator: this.validateControlledTransactionsInReport }),
    });

    this.primaryEntityList = this.data.primaryEntityList;
    this.constructPrimaryTradingTransactionMaps();
    this.workingContainer = this._sessionService.getWorkingContainer();
    this.firstPrimaryEntity = this.data.primaryEntityList[0];
    this.domicile = this.firstPrimaryEntity.domicile.name;
    this.countryId = this.firstPrimaryEntity.domicile.countryId;
    this.getPrimaryEntityCurrency();
    this.initEntityData();

    this.onDestroy$ = new Subject<null>();

    this.itemsPerPage = PaginationConstants.DEFAULT_PAGE_SIZE;

    this.getLatestGeneratedVersion();
  }

  initEntityData() {
    this.initTransactions();
    this.getCurrencyOptions();
  }

  getLatestGeneratedVersion(): void {
    if (this.data.report) {
      this.subscriptions.push(this._reportService.getReportInstance(this.data.report.reportId, this.data.reportInstanceId).subscribe((reportInstance) => {
        this.data.latestGeneratedVersion = reportInstance;
        this.getUpe();
        this.getLanguages();
        this.executiveSummaryConclusion = this.data.latestGeneratedVersion.executiveSummaryConclusion;
        this.executiveSummaryIntro = this.data.latestGeneratedVersion.executiveSummaryIntro;
        this.initPreSelectedTransactions();
        this.shouldDisplayPageNumber = this.data.latestGeneratedVersion.shouldDisplayPageNumber;
      }));
    }
  }

  initPreSelectedTransactions(): void {
    this.selectedPrimaryEntityIds = this.data.latestGeneratedVersion.primaryEntities.map(primaryEntity => primaryEntity.entityId);
    this.getPotentialTradingPartners();
    this.selectedTradingPartnerIds = this.data.latestGeneratedVersion.tradingPartners.map(tradingPartner => tradingPartner.entityId);
    this.tradingPartnersChange();
    this.data.latestGeneratedVersion.reportTransactions.forEach(reportTransaction => {
      this.selectableTransactions.forEach(transaction => {
        if (transaction.transactionId === reportTransaction.transactionId) {
          this.getControlledTransactionsInReportFormGroup().patchValue({ [transaction.transactionId]: true });
        }
      });
    });
    this.getCurrencyOptions();
  }

  initTransactions(): void {
    this.selectableTransactions = [];
    this.viewedTransactions = [];
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.subscriptions.forEach((sub) => {
      sub.unsubscribe();
    });
  }
  getFileFormControl(): AbstractControl { return this.uploadReportVersionForm.get('file'); }
  getLanguageFormControl(): AbstractControl { return this.uploadReportVersionForm.get('language'); }
  getCurrencyTypeFormControl(): AbstractControl { return this.uploadReportVersionForm.get('currencyType'); }
  getTradingPartnerEntityIdsFormControl(): AbstractControl { return this.uploadReportVersionForm.get('tradingPartnerEntityIds'); }
  getReportStageFormControl(): AbstractControl { return this.uploadReportVersionForm.get('reportStage'); }
  getPrimaryEntityIdsFormControl(): AbstractControl { return this.uploadReportVersionForm.get('primaryEntityIds'); }

  getControlledTransactionsInReportFormGroup(): FormGroup { return this.uploadReportVersionForm.get('controlledTransactionsInReport') as FormGroup; }

  getPageSizes(): number[] { return PaginationConstants.PAGE_SIZES; }

  getTransactionEntityCode(legalEntityTransactions: LegalEntityTransaction[], isSourceEntity: boolean): string {
    const transactionWithEntityCode = legalEntityTransactions.find(transaction => transaction.isSource === isSourceEntity);
    return transactionWithEntityCode.entity.code;
  }

  onFileSelect(selectedFiles: File[]): void {
    this.uploadedReport = selectedFiles[0];
  }

  save(event): void {
    event.preventDefault();
    Object.keys(this.uploadReportVersionForm.controls).forEach((controlKey) => {
      this.uploadReportVersionForm.controls[controlKey].markAsTouched();
    });

    if (this.uploadReportVersionForm.valid) {
      this.isSaveDisabled = true;
      this.getSignedUrlForUploadedReport();
    }
  }

  close(): void {
    this.closeAllModals();
  }

  private getLanguages(): void {
    const jurisdictionId = this.countryId;
    const taxYear = this.workingContainer.taxYear;

    this._compSearchService.getLanguages(jurisdictionId, taxYear).subscribe(({ data }) => {
      this.languages = data;

      this.languages.forEach((language) => {
        /* eslint camelcase: ['error', { allow: ['language_iso'] }] */
        language.displayName = this._translateService.instant(`internal_local_file_report.languages.${language.language_iso}`);
      });

      this.languages.sort((language1, language2) => {
        return language1.displayName > language2.displayName ? 1 : -1;
      });

      this.setDefaultLanguage(this.languages);
    });
  }

  private setDefaultLanguage(languages: LanguageIsoInfo[]): void {
    if (languages && languages.length > 0) {
      const languageToUseIsoInfo: LanguageIsoInfo = this._utilsService.findDefaultLanguage(languages, this.data.latestGeneratedVersion.languageIso);

      this.getLanguageFormControl().setValue(languageToUseIsoInfo.language_iso);
    }
  }

  private getCurrencyOptions(): void {
    let hasLegalEntityCurrencyConversion =
      this.selectedPrimaryEntityIds.some(selectedPrimaryEntityId => {
        return this.primaryEntityList.some(primaryEntity => {
          return !!(primaryEntity.entityId === selectedPrimaryEntityId && primaryEntity.legalEntityCurrencyConversion && primaryEntity.legalEntityCurrencyConversion.length);
        });
      });

    if (this.data && hasLegalEntityCurrencyConversion) {
      this.currencyOptions = this._utilsService.enumToArray(FinancialDataValueEnteredInEnum);
    } else {
      this.currencyOptions = [{ id: 1, name: FinancialDataValueEnteredInEnum[1] }];
    }
  }

  setDefaultCurrency(): void {
    const reportCurrencyIsoCode: string = this.data.latestGeneratedVersion.reportCurrency.isoCode;
    const mneCurrencyIsoCode: string = this.mneCurrency.isoCode;

    const currencyOptionNumber: number = reportCurrencyIsoCode === mneCurrencyIsoCode ?
      FinancialDataValueEnteredInEnum.MNE :
      FinancialDataValueEnteredInEnum.Entity;

    this.getCurrencyTypeFormControl().setValue(currencyOptionNumber);
  }

  private validateFile(fileFormControl: AbstractControl): ValidationErrors | null {
    let validationResult: ValidationErrors | null;

    if (this._utilsService.doesFilenameEndWithPdf(fileFormControl.value)) {
      validationResult = null;
    } else {
      validationResult = { validateFile: { valid: false } };
    }

    return validationResult;
  }

  private validateControlledTransactionsInReport(controlledTransactionsFormGroup: FormGroup): ValidationErrors | null {
    const allControlledTransactionIds = Object.keys(controlledTransactionsFormGroup.controls);
    const anyTransactionsAreSelected = allControlledTransactionIds.some((transactionId) => controlledTransactionsFormGroup.controls[transactionId].value === true);

    let validationResult: ValidationErrors | null;
    if (anyTransactionsAreSelected) {
      validationResult = null;
    } else {
      validationResult = { validateControlledTransactionsInReport: { valid: false } };
    }

    return validationResult;
  }

  private getSignedUrlForUploadedReport(): void {
    const uploadReportVersionPayload = this.createUploadReportVersionPayload();

    this._tpSafeService.getSignedUrlForUploadedReport(uploadReportVersionPayload).subscribe(
      ({ data }) => {
        this.saveUploadedReportToTpSafe(data.url);
        this._reportService.updateRepostActiveStatus(data.internalLocalfileReportId, data.internalLocalfileReportInstanceId, this.workingContainer.containerId).subscribe(()=>{});

      },
      () => this.isSaveDisabled = false
    );
  }

  private saveUploadedReportToTpSafe(signedUrl: string): void {
    this._tpSafeService.saveUploadedReportToTpSafe(signedUrl, this.uploadedReport).subscribe(
      () => {
        this.isSaveDisabled = false;
        this.success();
        this.close();
      },
      () => this.isSaveDisabled = false
    );
  }

  private createUploadReportVersionPayload(): UploadReportVersionPayload {
    const allControlledTransactionsInReport: Record<string, boolean> = this.uploadReportVersionForm.value.controlledTransactionsInReport;
    const selectedTransactionIds = Object.keys(allControlledTransactionsInReport)
      .filter(transactionId => allControlledTransactionsInReport[transactionId] === true)
      .map(Number);

    return {
      reportStage: this.getReportStageFormControl().value,
      currencyId: this.getUploadReportPayloadCurrencyId(),
      internalLocalfileReportId: this.data.report.reportId,
      primaryEntityIds: this.selectedPrimaryEntityIds,
      primaryEntityId: this.selectedPrimaryEntityIds[0],
      tradingPartnerIds: this.selectedTradingPartnerIds, // Workaround until we fix doc-stack
      tradingPartnerEntityId: this.selectedTradingPartnerIds[0], // Workaround until we fix doc-stack
      executiveSummaryIntro: this.executiveSummaryIntro,
      executiveSummaryConclusion: this.executiveSummaryConclusion,
      languageIso: this.getLanguageFormControl().value,
      containerId: this.workingContainer.containerId,
      isUploadedReport: true,
      selectedTransactionIds,
      mimeType: this.uploadedReport.type,
      fileExtension: FileConstants.FILE_EXTENSIONS.PDF,
      isoCode: this.firstPrimaryEntity.domicile.isoCode,
      domicileName: this.firstPrimaryEntity.domicile.name,
      domicileId: this.firstPrimaryEntity.domicile.countryId,
      shouldDisplayPageNumber: this.shouldDisplayPageNumber
    };
  }

  private getUploadReportPayloadCurrencyId(): number {
    let currencyId: number;
    const currencyTypeId = Number(this.getCurrencyTypeFormControl().value);

    if (currencyTypeId === 1) {
      currencyId = this.mneCurrency.currencyId;
    } else if (currencyTypeId === 2) {
      currencyId = this.taxJurisdictionCurrency.currencyId;
    }

    return currencyId;
  }

  private getMneCurrencyInfo(currencyId: number): void {
    this._currenciesService.show(currencyId).subscribe((data) => {
      this.mneCurrency = data.data;
      this.setDefaultCurrency();
    });
  }

  private getPrimaryEntityCurrency(): void {
    this._countriesService.show(this.countryId).subscribe(({ data }: ObjectResponse<Country>) => {
      this.taxJurisdictionCurrency = data.defaultCurrency;
    });
  }

  private getUpe(): void {
    this._entitiesService.getUpe().subscribe((data) => {
      this.upe = data.data;
      this.getMneCurrencyInfo(this.upe.upeCurrency.currencyId);
    });
  }

  primaryEntitiesChange(): void {
    this.initEntityData();
    this.getPotentialTradingPartners();
    this.tradingPartnersChange();
  }

  filterSelectedTradingPartners(potentialTradingPartnerIdsArr: number[]): void {
    this.selectedTradingPartnerIds = this.selectedTradingPartnerIds.filter(selectedTradingPartnerId =>
      potentialTradingPartnerIdsArr.includes(selectedTradingPartnerId)
    );
  }

  getPotentialTradingPartners(): void {
    let potentialTradingPartnerIds = new Set<number>([]);
    this.selectedPrimaryEntityIds.forEach(selectedPrimaryEntityId => {
      if (this.primaryTradingPartnerMap[selectedPrimaryEntityId] && this.primaryTradingPartnerMap[selectedPrimaryEntityId].length) {
        this.primaryTradingPartnerMap[selectedPrimaryEntityId].forEach(tradingPartnerId => {
          potentialTradingPartnerIds.add(tradingPartnerId);
        });
      }
    });

    const potentialTradingPartnerIdsArr = Array.from(potentialTradingPartnerIds);
    this.potentialTradingPartners = potentialTradingPartnerIdsArr.map(potentialTradingPartnerId => {
      return this.tradingPartnerEntities[potentialTradingPartnerId];
    });

    this.potentialTradingPartners.sort((tradingPartner1, tradingPartner2) => {
      return tradingPartner1.code > tradingPartner2.code ? 1 : -1;
    });

    this.filterSelectedTradingPartners(potentialTradingPartnerIdsArr);
  }

  constructPrimaryTradingTransactionMaps(): void {
    this.data.containerTransactions.forEach(containerTransaction => {
      const sourceEntityId = containerTransaction.legalEntityTransactions[0].entity ? containerTransaction.legalEntityTransactions[0].entity.entityId : null;
      const destEntityId = containerTransaction.legalEntityTransactions[1].entity ? containerTransaction.legalEntityTransactions[1].entity.entityId : null;
      const primaryEntityIds = this.primaryEntityList.map(primaryEntity => primaryEntity.entityId);

      if (primaryEntityIds.includes(sourceEntityId)) {
        this.mapPrimaryTradingPartners(sourceEntityId, destEntityId);
        this.tradingPartnerEntities[destEntityId] = containerTransaction.legalEntityTransactions[1].entity;
        const transactionTag = `${sourceEntityId}-${destEntityId}`;
        this.createPrimaryTradingPartnerTransactionMap(transactionTag, containerTransaction);
      }
      if (primaryEntityIds.includes(destEntityId)) {
        this.mapPrimaryTradingPartners(destEntityId, sourceEntityId);
        this.tradingPartnerEntities[sourceEntityId] = containerTransaction.legalEntityTransactions[0].entity;
        const transactionTag = `${destEntityId}-${sourceEntityId}`;
        this.createPrimaryTradingPartnerTransactionMap(transactionTag, containerTransaction);
      }
    });
  }

  mapPrimaryTradingPartners(primaryEntityId: number, tradingPartnerId: number): void {
    this.primaryTradingPartnerMap[primaryEntityId] = this.primaryTradingPartnerMap[primaryEntityId] || [];
    this.primaryTradingPartnerMap[primaryEntityId].push(tradingPartnerId);
  }

  createPrimaryTradingPartnerTransactionMap(transactionTag: string, containerTransaction: Transaction) {
    this.primaryTradingPartnerTransactionMap[transactionTag] = this.primaryTradingPartnerTransactionMap[transactionTag] || [];
    this.primaryTradingPartnerTransactionMap[transactionTag].push(containerTransaction);
  }

  tradingPartnersChange() {
    if (this.previousSelectedTradingPartnerIds.length > this.selectedTradingPartnerIds.length) {
      const changedTradingPartnerIds = [];
      this.previousSelectedTradingPartnerIds.forEach(previousSelectedTradingPartnerId => {
        if (!this.selectedTradingPartnerIds.includes(previousSelectedTradingPartnerId)) {
          changedTradingPartnerIds.push(previousSelectedTradingPartnerId);
        }
      });

      this.removeSelectedTransactions(changedTradingPartnerIds);
    }
    this.previousSelectedTradingPartnerIds = this.selectedTradingPartnerIds;

    this.selectableTransactions = [];
    this.getPrimaryTradingPairTransactions();
    this.totalItems = this.selectableTransactions.length;
    this.viewedTransactions = this.selectableTransactions.slice(0, this.itemsPerPage);

    this.selectableTransactions.forEach((transaction) => {
      this.getControlledTransactionsInReportFormGroup().addControl(String(transaction.transactionId), this._formBuilder.control(''));
    });
  }

  getPrimaryTradingPairTransactions(): void {
    this.selectedPrimaryEntityIds.forEach(selectedPrimaryEntityId => {
      this.selectedTradingPartnerIds.forEach(selectedtradingPartner => {
        const transactionTag = `${selectedPrimaryEntityId}-${selectedtradingPartner}`;
        if (this.primaryTradingPartnerTransactionMap[transactionTag]) {
          this.primaryTradingPartnerTransactionMap[transactionTag].forEach(transaction => {
            if (!this.selectableTransactions.includes(transaction)) {
              this.selectableTransactions.push(transaction);
            }
          });
        }
      });
    });
  }

  removeSelectedTransactions(changedTradingPartners) {
    changedTradingPartners.forEach(tradingPartnerId => {
      const partialTransactionKey = `-${tradingPartnerId}`;
      Object.keys(this.primaryTradingPartnerTransactionMap).forEach(key => {
        if (key.includes(partialTransactionKey)) {
          this.primaryTradingPartnerTransactionMap[key].forEach(transaction => {
            this.getControlledTransactionsInReportFormGroup().removeControl(String(transaction.transactionId));
          });
        }
      });
    });
  }

  pageChanged(event: any): void {
    const currentPage = parseInt(event.page, 10);
    const itemsPerPage = parseInt(event.itemsPerPage, 10);
    this.viewedTransactions = this.selectableTransactions.slice((currentPage - 1) * itemsPerPage, (currentPage - 1) * itemsPerPage + itemsPerPage);
  }

  pageSizeChanged(itemsPerPage: string): void {
    const pageSize = parseInt(itemsPerPage, 10);
    this.itemsPerPage = pageSize;
    this.viewedTransactions = this.selectableTransactions.slice(0, pageSize);
  }
}
